import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useSearchParams } from 'react-router-dom';

import { Flex, Loading, NotFound, Page, RichContent, Section } from 'components';
import { URLParams, request } from 'app/utils';
import { ProductDetailsApiResponse, ProductEnrollment, ProductHero, ProductsContinuationApiResponse, useProducts } from 'features/products';
import { useSchool } from 'features/school';
import { MasterclassDownloads } from 'features/masterclass';

import { ModulesDetailsApiResponse } from 'features/modules';
import { useContents } from 'features/contents';
import { useDownloads } from 'features/downloads';

const Wrapper = styled(Flex)`
  flex-grow: 1;
  flex-direction: column;
  position: relative;

  ${(props: any) => {
    if (props['data-image']) {
      return `
        background-image: url(${props['data-image']});
        background-size: cover;
        background-position: top center;
        background-repeat: no-repeat;
        background-color: ${props.theme.colors.dark_350};
      `;
    } else {
      return `
        background-color: ${props['data-schoolalias'] === 'b3' ? props.theme.colors.dark_350_70 : props.theme.colors.secondary_400};
      `;
    }
  }}

  ${({ theme: { colors, mediaQueries } }) => {
    return `
      ${mediaQueries.mobile} {
        &::after {
          content: '';
          display: block;
          position: absolute;
          width: 100%;
          height: 100%;
          bottom: 0;
          background: ${colors.dark_350};
          z-index: 1;
          opacity: 0.8;
        }
      }

      ${mediaQueries.desktop} {
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          width: 100%;
          height: 740px;
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 50%, rgba(23, 25, 24, 0.5) 75%, rgba(23, 25, 24, 1) 100%);
          z-index: 1;
        }

        &::after {
          content: '';
          display: block;
          position: absolute;
          width: 100%;
          height: calc(100% - 740px);
          bottom: 0;
          background: ${colors.dark_350};
          z-index: 1;
          opacity: 1;
        }
      }
    `;
  }}
`;

interface DetailsProps {
  enroll?: boolean;
}

const Details = ({ enroll = false }: DetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { productSlug, productClassUuid } = useParams();

  const { apiGatewayUrl, schoolAlias } = useSchool();

  const { productDetails, setProductDetails, clearProductDetails, setProductsContinuation, clearProductsContinuation } = useProducts();
  const { setContentsList, clearContentsList } = useContents();
  const { downloads, setDownloads } = useDownloads();

  const [searchParams] = useSearchParams();
  const src = searchParams.get('src') || '';

  const fetchMasterclass = useCallback(
    async (forceEnroll = false) => {
      setLoading(true);

      const params = URLParams({
        enroll: forceEnroll || enroll,
        src,
      });

      await request<ProductDetailsApiResponse>({
        url: `/api/v1/School/Products/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}&${params}` : `?${params}`}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      })
        .then(async ({ data }) => {
          setProductDetails(data);
          setDownloads(data.downloads);

          await Promise.all([
            data.enrolled
              ? request<ProductsContinuationApiResponse>({
                  url: `/api/v1/School/ProductContentNextItems/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
                  method: 'get',
                  baseURL: apiGatewayUrl,
                }).then((response) => {
                  setProductsContinuation(response.data);
                })
              : null,

            request<ModulesDetailsApiResponse>({
              url: `/api/v1/School/ProductContentTimeline/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            }).then((response) => {
              setContentsList(response.data.items);
            }),
          ]);
        })
        .catch(() => {
          setNotFound(true);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [productSlug, productClassUuid, src]
  );

  useEffect(() => {
    fetchMasterclass();
  }, [productSlug, productClassUuid, src]);

  useEffect(() => {
    return () => {
      clearProductDetails();
      clearProductsContinuation();
      clearContentsList();
    };
  }, []);

  if (notFound) return <NotFound />;

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page>
      <Wrapper data-image={productDetails?.product?.coverImageUrl} data-schoolalias={schoolAlias}>
        <Flex flexDirection="column" zIndex={5}>
          <Flex flexDirection="column" flexGrow={0} minHeight={{ d: 550 }}>
            <ProductHero />
            <ProductEnrollment productSlug={productSlug} productClassUuid={productClassUuid} fecthApi={fetchMasterclass} />
          </Flex>

          <Section flexGrow={0} flexDirection="column" pt={{ m: 50, d: 100 }} pb={{ m: 80, d: downloads?.length ? 200 : 90 }}>
            <RichContent currentTheme="dark">{productDetails?.course?.longHeaderDescription}</RichContent>

            <MasterclassDownloads />
          </Section>
        </Flex>
      </Wrapper>
    </Page>
  );

  // <Navigate to={createUrl('dashboard')} replace />
};

export default Details;
